import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  defaultFollowUpOptions,
  FOLLOW_UP_TIME_OPTIONS,
  FOLLOW_UP_WHEN_OPTIONS,
  FollowUpMenuItem,
  FollowUpOption,
} from '@models/follow-up-options.model';
import { FollowUpOptionsService } from '@services/api/follow-up-options.service';
import { ToasterService } from '@services/util/toaster.service';
import { Subscription } from 'rxjs';

/**
 * The agent's own Follow Up menu presets, edited from the cog in the menu itself.
 *
 * It lives here rather than under Settings because agents don't have access to the Settings area —
 * the menu they are personalising is the only place they can reach these from.
 */
@Component({
  selector: 'app-follow-up-options-editor',
  templateUrl: './follow-up-options-editor.component.html',
  styleUrls: ['./follow-up-options-editor.component.scss'],
})
export class FollowUpOptionsEditorComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  @ViewChild('customLabelInput') customLabelInput: ElementRef<HTMLInputElement>;

  readonly whenOptions = FOLLOW_UP_WHEN_OPTIONS;
  readonly timeOptions = FOLLOW_UP_TIME_OPTIONS;
  readonly labelMaxLength = 30;
  // Mirrors the backend's cap on PUT /follow-up-options.
  readonly maxOptions = 20;

  options: FollowUpOption[] = [];
  previewItems: FollowUpMenuItem[] = [];
  isLoading = true;
  isSaving = false;
  submitted = false;

  customOption = { label: '', when: null, time: '08:00' };
  customSubmitted = false;

  private loadSubscription: Subscription;

  constructor(
    private followUpOptionsService: FollowUpOptionsService,
    private toaster: ToasterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isVisible && this.isVisible) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  onVisibleChange(visible: boolean): void {
    this.isVisible = visible;
    this.isVisibleChange.emit(visible);
  }

  close(): void {
    this.onVisibleChange(false);
  }

  /** Every edit re-renders the preview, since a label, day or time all change how the menu reads. */
  onOptionChange(): void {
    this.refreshPreview();
  }

  drop(event: CdkDragDrop<FollowUpOption[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.options, event.previousIndex, event.currentIndex);
    this.refreshPreview();
  }

  removeOption(option: FollowUpOption): void {
    this.options = this.options.filter(itm => itm !== option);
    this.refreshPreview();
  }

  focusCustomForm(): void {
    const input = this.customLabelInput?.nativeElement;
    input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input?.focus();
  }

  addCustomOption(): void {
    this.customSubmitted = true;
    const label = (this.customOption.label || '').trim();
    if (!label || !this.customOption.when || !this.customOption.time) {
      return;
    }
    if (this.options.length >= this.maxOptions) {
      this.toaster.Error(`You can have up to ${this.maxOptions} follow up options`);
      return;
    }

    this.options = [
      ...this.options,
      { id: `custom_${Date.now()}`, label, isEnabled: true, when: this.customOption.when, time: this.customOption.time },
    ];
    this.customOption = { label: '', when: null, time: '08:00' };
    this.customSubmitted = false;
    this.refreshPreview();
  }

  resetToDefault(): void {
    this.options = defaultFollowUpOptions();
    this.submitted = false;
    this.refreshPreview();
  }

  save(): void {
    // Save stays clickable throughout, so guard here: saving mid-load would PUT the empty
    // placeholder list and wipe the agent's options, and a second click mid-save would double-submit.
    if (this.isLoading || this.isSaving) {
      return;
    }
    this.submitted = true;
    if (this.options.some(option => !option.label?.trim() || !option.when || !option.time)) {
      this.toaster.Error('Every option needs a label, a day and a time');
      return;
    }

    this.isSaving = true;
    const payload = this.options.map(option => ({ ...option, label: option.label.trim() }));
    this.followUpOptionsService.save(payload).subscribe({
      next: saved => {
        this.isSaving = false;
        this.submitted = false;
        // The stored copy can differ from what was sent (the backend trims labels, drops unknown keys).
        this.options = saved.map(option => ({ ...option }));
        this.toaster.Success('Follow up options saved');
        this.close();
      },
      error: err => {
        this.isSaving = false;
        this.toaster.Error(err?.message || 'Failed to save follow up options');
      },
    });
  }

  trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  /** Always re-read on open so two tabs, or a save from another lead, can't be silently overwritten. */
  private load(): void {
    this.isLoading = true;
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.followUpOptionsService.load().subscribe({
      next: options => this.applyOptions(options),
      // ApiService.get rethrows the raw HttpErrorResponse (unlike put, which unwraps to the body),
      // so the backend's sentence — e.g. the 403 "This office is not one of your offices." — is on `.error`.
      error: err => {
        this.toaster.Error(err?.error?.message || 'Could not load your saved follow up options — showing the current menu instead');
        this.applyOptions(this.followUpOptionsService.options);
      },
    });
  }

  private applyOptions(options: FollowUpOption[]): void {
    this.options = options.map(option => ({ ...option }));
    this.submitted = false;
    this.customSubmitted = false;
    this.customOption = { label: '', when: null, time: '08:00' };
    this.isLoading = false;
    this.refreshPreview();
  }

  private refreshPreview(): void {
    this.previewItems = this.followUpOptionsService.menuItems(this.options);
  }
}
